import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AlertCircle, Loader2, Eye, EyeOff } from 'lucide-react';
import Logo from '../components/site/Logo';
import { useAuth } from '../lib/AuthContext';
import { supabase, PENDING_PROMPT_KEY } from '../lib/supabase';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PENDING_INTAKE_KEY = 'yb.pending_intake_v1';

const deriveName = (prompt) => {
  if (!prompt) return 'Untitled project';
  const cleaned = prompt.replace(/\s+/g, ' ').trim();
  return cleaned.length > 60 ? cleaned.slice(0, 57) + '\u2026' : cleaned || 'Untitled project';
};

const deriveNameFromIntake = (intake) => {
  if (!intake) return 'Untitled project';
  const biz = (intake.business_name || '').trim();
  if (biz) return biz.length > 60 ? biz.slice(0, 57) + '…' : biz;
  const tradeRaw = intake.trade === 'other'
    ? (intake.trade_other || 'Trade')
    : (intake.trade || 'Trade');
  const trade = tradeRaw ? `${tradeRaw[0].toUpperCase()}${tradeRaw.slice(1)}` : 'Trade';
  const town = (intake.town || 'Australia').trim();
  const base = `${trade} in ${town}`;
  return base.length > 60 ? base.slice(0, 57) + '…' : base;
};

const savePendingPrompt = async (userId) => {
  const prompt = sessionStorage.getItem(PENDING_PROMPT_KEY);
  if (!prompt || !userId) return null;
  const { data, error } = await supabase
    .from('user_projects')
    .insert({ user_id: userId, name: deriveName(prompt), prompt })
    .select()
    .single();
  if (!error) sessionStorage.removeItem(PENDING_PROMPT_KEY);
  return { data, error };
};

// Resume a Guided Intake submitted before signup: create the project and
// kick off `/generate-guided` so the user lands on a building page.
const resumePendingIntake = async (userId) => {
  let raw = null;
  try { raw = sessionStorage.getItem(PENDING_INTAKE_KEY); } catch (_) {}
  if (!raw || !userId) return null;
  let intake;
  try { intake = JSON.parse(raw); } catch (_) { intake = null; }
  try { sessionStorage.removeItem(PENDING_INTAKE_KEY); } catch (_) {}
  if (!intake || !intake.trade || !intake.town) return null;

  const { data: inserted, error } = await supabase
    .from('user_projects')
    .insert({ user_id: userId, name: deriveNameFromIntake(intake), prompt: '' })
    .select('id')
    .single();
  if (error || !inserted) return { error };
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;
    if (token) {
      await axios.post(
        `${API}/projects/${inserted.id}/generate-guided`,
        intake,
        { headers: { Authorization: `Bearer ${token}` }, timeout: 20000 },
      );
    }
  } catch (_) { /* project exists — user can retry from project page */ }
  return { data: inserted };
};

const AuthShell = ({ mode }) => {
  const isSignup = mode === 'signup';
  const { signIn, signUp, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const nextPath = params.get('next') || '/projects';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [resetState, setResetState] = useState('idle'); // idle | loading | sent
  const [resetError, setResetError] = useState('');

  useEffect(() => {
    if (!authLoading && user) {
      (async () => {
        // Guided-intake resume takes priority — if present, land on the new project.
        const intakeResult = await resumePendingIntake(user.id);
        if (intakeResult?.data?.id) {
          navigate(`/projects/${intakeResult.data.id}`, { replace: true });
          return;
        }
        await savePendingPrompt(user.id);
        navigate(nextPath, { replace: true });
      })();
    }
  }, [authLoading, user, navigate, nextPath]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    setStatus('loading');
    try {
      if (isSignup) {
        const data = await signUp(email.trim(), password);
        if (data?.user && !data?.session) {
          setStatus('idle');
          setInfo("Almost there \u2014 check your inbox to confirm your email, then log in.");
          return;
        }
      } else {
        await signIn(email.trim(), password);
      }
    } catch (err) {
      setStatus('idle');
      const msg = err?.message || 'Something went wrong. Please try again.';
      if (/registered|already/i.test(msg)) {
        setError('That email is already registered. Try logging in instead.');
      } else if (/invalid login|invalid credentials|invalid password/i.test(msg)) {
        setError('Wrong email or password.');
      } else {
        setError(msg);
      }
    }
  };

  const pendingPrompt = sessionStorage.getItem(PENDING_PROMPT_KEY);

  const sendResetEmail = async () => {
    setResetError('');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setResetError('Enter your email above first, then click "Forgot password?" again.');
      return;
    }
    setResetState('loading');
    try {
      const redirectTo = `${window.location.origin}/reset-password`;
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), { redirectTo });
      if (error) throw error;
      setResetState('sent');
    } catch (err) {
      setResetState('idle');
      setResetError(err?.message || 'Could not send reset email.');
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'var(--yb-bg)', color: 'var(--yb-text)' }}>
      <header className="px-6 lg:px-10 py-6">
        <Link to="/" style={{ color: 'var(--yb-ink)' }} className="inline-flex"><Logo /></Link>
      </header>
      <main className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-md">
          <p className="text-[12.5px] uppercase tracking-widest mb-3" style={{ color: 'var(--yb-muted)' }}>
            {isSignup ? 'Create an account' : 'Welcome back'}
          </p>
          <h1 className="font-serif text-4xl md:text-5xl leading-tight tracking-tight" style={{ color: 'var(--yb-ink)' }}>
            {isSignup ? (<>Start <span className="italic">building.</span></>) : (<>Log <span className="italic">in.</span></>)}
          </h1>
          {pendingPrompt && isSignup && (
            <div className="mt-5 p-3 rounded-xl border text-[13px]" style={{ borderColor: 'var(--yb-border)', backgroundColor: '#EEF2FF', color: 'var(--yb-ink)' }}>
              <span className="font-medium">We’ll save your idea after signup:</span>{' '}
              <span style={{ color: 'var(--yb-muted)' }}>&ldquo;{pendingPrompt.slice(0, 120)}{pendingPrompt.length > 120 ? '\u2026' : ''}&rdquo;</span>
            </div>
          )}

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="block text-[12px] uppercase tracking-wider mb-1.5" style={{ color: 'var(--yb-muted)' }}>Email</label>
              <input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@yourbusiness.com"
                className="w-full px-4 py-2.5 rounded-lg border text-[15px] outline-none bg-white"
                style={{ borderColor: 'var(--yb-border)', color: 'var(--yb-ink)' }}
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-[12px] uppercase tracking-wider" style={{ color: 'var(--yb-muted)' }}>Password</label>
                {!isSignup && (
                  <button
                    type="button"
                    onClick={sendResetEmail}
                    disabled={resetState === 'loading' || resetState === 'sent'}
                    className="text-[12.5px] font-medium disabled:opacity-70"
                    style={{ color: 'var(--yb-indigo)' }}
                  >
                    {resetState === 'loading' ? 'Sending…' : resetState === 'sent' ? 'Email sent' : 'Forgot password?'}
                  </button>
                )}
              </div>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  autoComplete={isSignup ? 'new-password' : 'current-password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  className="w-full pl-4 pr-11 py-2.5 rounded-lg border text-[15px] outline-none bg-white"
                  style={{ borderColor: 'var(--yb-border)', color: 'var(--yb-ink)' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                  className="yb-eye-toggle absolute top-1/2 -translate-y-1/2 right-2 inline-flex items-center justify-center w-8 h-8 rounded-md transition-colors"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={status === 'loading'}
              className="yb-btn-primary w-full py-2.5 rounded-lg font-semibold text-[14.5px] flex items-center justify-center gap-1.5 disabled:opacity-70"
            >
              {status === 'loading' && <Loader2 size={15} className="animate-spin" />}
              {status === 'loading' ? (isSignup ? 'Creating account\u2026' : 'Signing in\u2026') : (isSignup ? 'Create account' : 'Log in')}
            </button>
          </form>

          {error && (
            <p className="mt-4 text-[13px] flex items-center gap-2" style={{ color: '#B4530A' }}>
              <AlertCircle size={14} /> {error}
            </p>
          )}
          {info && (
            <p className="mt-4 text-[13px] flex items-start gap-2" style={{ color: 'var(--yb-indigo)' }}>
              <AlertCircle size={14} className="mt-0.5" /> {info}
            </p>
          )}
          {resetState === 'sent' && !isSignup && (
            <p className="mt-4 text-[13px] flex items-start gap-2" style={{ color: 'var(--yb-indigo)' }}>
              <AlertCircle size={14} className="mt-0.5" /> Password reset email sent. Check your inbox — click the link to set a new password.
            </p>
          )}
          {resetError && !isSignup && (
            <p className="mt-4 text-[13px] flex items-center gap-2" style={{ color: '#B4530A' }}>
              <AlertCircle size={14} /> {resetError}
            </p>
          )}

          <p className="mt-8 text-[13.5px]" style={{ color: 'var(--yb-muted)' }}>
            {isSignup ? (
              <>Already have an account?{' '}<Link to={`/login${location.search}`} className="font-medium" style={{ color: 'var(--yb-indigo)' }}>Log in</Link></>
            ) : (
              <>New here?{' '}<Link to={`/signup${location.search}`} className="font-medium" style={{ color: 'var(--yb-indigo)' }}>Create an account</Link></>
            )}
          </p>
        </div>
      </main>
    </div>
  );
};

export const Login = () => <AuthShell mode="login" />;
export const Signup = () => <AuthShell mode="signup" />;

export default AuthShell;
